/**
 * CTAテンプレート: the invitation an article ends with.
 *
 * A table was the wrong shape here for a reason particular to this data. A CTA
 * is not a record about the clinic - it is a piece of the published page, and
 * the only question anybody has about one is "what does this look like at the
 * end of an article". Five truncated columns cannot answer that; the card can,
 * so the card is what this screen shows, built from the same `.ctabox` markup
 * and the same stylesheet rules the editor inserts and the exporter publishes.
 *
 * Same shape as 監修医 and ペルソナ: a grid with the way to add one first, a sheet
 * to read, a dialog to edit, and 承認 as its own decision.
 */
import { confirmDialog } from '../components/AppDialog.tsx';
import { useId, useState } from 'react';
import { useLoaderData, useOutletContext, useRevalidator, useSearchParams } from 'react-router';
import { del, patch, post, type CatalogItem, type Session } from '../api.ts';
import { inScope, sessionScope, type FactPanelProps } from '../fact-panel.ts';
import { Card, Pill, StatusPill } from '../components/ui.tsx';
import { Modal, ModalForm } from '../components/Modal.tsx';
import { Field, FieldGroup } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { AddFromLink } from '../components/AddFromLink.tsx';
import { AnimatePresence, listItem, m } from '../components/motion.tsx';
import { SkeletonPeople, usePendingScreen } from '../components/Skeleton.tsx';
import { CTA_CHANNELS, channelLabels } from '../../../shared/cta-channels.ts';

interface Cta extends CatalogItem {
  storeName?: string | null;
  name?: string;
  heading?: string;
  body?: string;
  buttonLabel?: string;
  buttonUrl?: string;
  channels?: unknown;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const channelsOf = (cta: Cta): string[] =>
  Array.isArray(cta.channels)
    ? cta.channels.filter((entry): entry is string => typeof entry === 'string')
    : [];

const EMPTY = {
  name: '',
  heading: '',
  body: '',
  buttonLabel: '',
  buttonUrl: '',
  channels: [] as string[],
};

type Form = typeof EMPTY;

function formFor(cta: Cta): Form {
  return {
    name: text(cta.name),
    heading: text(cta.heading),
    body: text(cta.body),
    buttonLabel: text(cta.buttonLabel),
    buttonUrl: text(cta.buttonUrl),
    channels: channelsOf(cta),
  };
}

/**
 * The CTA as an article publishes it.
 *
 * Deliberately the same class names as insert-markdown.ts's `ctaBlock`, so this
 * is not a drawing of the card but the card. The button is rendered as a
 * `<span>`: it is a picture of a link, and a real anchor here would take
 * somebody editing their booking page to their booking page.
 */
function CtaCard({ cta }: { cta: Cta }) {
  // Blank stays blank, exactly as ctaBlock publishes it.
  const heading = text(cta.heading);
  const body = text(cta.body);
  const label = text(cta.buttonLabel) || '詳しくはこちら';
  const channels = channelLabels(channelsOf(cta));

  return (
    <div className="ctabox">
      {heading !== '' && <p className="ctabox__title">{heading}</p>}
      {body !== '' && <p>{body}</p>}
      <p>
        <span className="ctabox__button">{label}</span>
      </p>
      {channels.length > 0 && (
        <p className="ctabox__channels">ご予約・ご相談：{channels.join('／')}</p>
      )}
    </div>
  );
}

function CtaTile({ cta, index, onOpen }: { cta: Cta; index: number; onOpen: () => void }) {
  const name = text(cta.name) || '（名称なし）';
  const channels = channelLabels(channelsOf(cta));

  return (
    <m.button
      className="ptile ptile--cta"
      type="button"
      data-motion
      layout
      {...listItem(index)}
      onClick={onOpen}
    >
      {/*
        The name and where it applies, and nothing else. The tile used to lead
        with a picture of the button and a line of the text underneath -
        「詳しくはこちら」 and 「まだ内容がありません」 on every empty template -
        which was noise on a list somebody scans for one name. The CTA itself
        is what opening the tile shows.
      */}
      <span className="ptile__label">{name}</span>

      {channels.length > 0 && (
        <span className="ptile__facts">
          {channels.map((channel) => (
            <span key={channel} className="tag">
              {channel}
            </span>
          ))}
        </span>
      )}

      <span className="ptile__meta">
        {cta.storeId === null ? (
          <Pill>クリニック共通</Pill>
        ) : (
          <Pill tone="muted">{cta.storeName ?? '店舗'}</Pill>
        )}
        <StatusPill status={cta.status} />
      </span>
    </m.button>
  );
}

export default function Ctas() {
  const { items } = useLoaderData<{ items: Cta[] }>();
  const session = useOutletContext<Session>();
  return <CtasPanel items={items} base="" scope={sessionScope(session)} />;
}

/**
 * The screen itself, on the clinic's route and on the JMC clinic page alike.
 * See fact-panel.ts for the three things that differ between the two.
 */
export function CtasPanel({ items: rows, base, scope, clinicId, lead }: FactPanelProps<Cta>) {
  const revalidator = useRevalidator();
  const { isClinicAdmin, stores: ownStores, activeStore } = scope;

  const [newScope, setNewScope] = useState<string>(isClinicAdmin ? '' : (ownStores[0]?.id ?? ''));

  const [params, setParams] = useSearchParams();
  const viewScope = params.get('scope') ?? (activeStore ? 'effective' : 'all');
  const items = rows.filter((row) => inScope(row, viewScope, activeStore?.id));
  const scopeLabelId = useId();
  const channelLabelId = useId();

  const setViewScope = (next: string) => {
    const query = new URLSearchParams(params);
    if (next === (activeStore ? 'effective' : 'all')) query.delete('scope');
    else query.set('scope', next);
    setParams(query);
  };

  const scopeTabs = activeStore
    ? [
        { id: 'effective', label: `共通 ＋ ${activeStore.name}` },
        { id: 'clinic', label: 'クリニック共通' },
        { id: 'store', label: activeStore.name },
      ]
    : [
        { id: 'all', label: '共通 ＋ 全店舗' },
        { id: 'clinic', label: 'クリニック共通' },
      ];

  /** null = closed, 'new' = creating, otherwise the CTA being edited. */
  const [open, setOpen] = useState<'new' | Cta | null>(null);
  /** The one being READ. Looking at a CTA and rewriting it are different acts. */
  const [viewing, setViewing] = useState<Cta | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const pending = usePendingScreen();

  const editing = open !== 'new' && open !== null ? open : null;

  function start(target: 'new' | Cta) {
    setForm(target === 'new' ? EMPTY : formFor(target));
    setViewing(null);
    setOpen(target);
  }

  const toggleChannel = (value: string) =>
    setForm((current) => ({
      ...current,
      channels: current.channels.includes(value)
        ? current.channels.filter((entry) => entry !== value)
        : [...current.channels, value],
    }));

  const save = () => {
    const payload = {
      name: form.name,
      heading: form.heading,
      body: form.body,
      buttonLabel: form.buttonLabel,
      // null, never '': "no link" is NULL in the database. See catalog/routes.ts.
      buttonUrl: form.buttonUrl.trim() || null,
      channels: form.channels,
    };
    if (editing) return patch(`${base}/cta-templates/${editing.id}`, payload);
    return post(`${base}/cta-templates`, newScope ? { ...payload, storeId: newScope } : payload);
  };

  /** Approve from the sheet, where the decision is made. See Supervisors.tsx. */
  async function approve(cta: Cta | null, status: 'APPROVED' | 'DRAFT') {
    if (!cta) return;
    setBusy(true);
    try {
      await patch(`${base}/cta-templates/${cta.id}`, { status });
      await revalidator.revalidate();
      setViewing({ ...cta, status });
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(status: 'APPROVED' | 'DRAFT') {
    if (!editing) return;
    setBusy(true);
    try {
      await patch(`${base}/cta-templates/${editing.id}`, { status });
      await revalidator.revalidate();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!editing) return;
    const ok = await confirmDialog({
      title: 'このCTAを削除しますか？',
      message: `「${form.name || editing.name || 'このCTA'}」を削除します。元に戻せません。記事にすでに入れたCTAはそのまま残ります。`,
      confirmLabel: '削除する',
      danger: true,
    });
    if (!ok) return;
    /*
     * /permanent. The bare DELETE only RETIRES a fact - it stays in the list
     * with a 廃止 pill - so this button, which says 「元に戻せません」, visibly
     * did nothing: the request succeeded and the tile stayed. Articles that
     * picked it keep their text; the reference is ON DELETE SET NULL.
     */
    await del(`${base}/cta-templates/${editing.id}/permanent`);
    setOpen(null);
    await revalidator.revalidate();
  }

  // https only, and said before the form is submitted rather than as a 400.
  const urlOk = form.buttonUrl === '' || /^https:\/\//i.test(form.buttonUrl.trim());

  return (
    <Card
      lead={lead}
      title="CTAテンプレート"
      caption="記事の最後に置く予約への案内です。承認したものだけが記事で選べます。リンクは https のみ受け付けます。"
      actions={
        <AddFromLink
          resource="cta-templates"
          storeId={newScope || undefined}
          {...(clinicId ? { clinicId } : {})}
          onQueued={() => void revalidator.revalidate()}
        />
      }
    >
      {(isClinicAdmin || activeStore) && (
        <div className="scopebar">
          <span className="scopebar__label" id={scopeLabelId}>
            表示
          </span>
          <div className="scopebar__options" role="group" aria-labelledby={scopeLabelId}>
            {scopeTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className="scopeopt"
                aria-pressed={tab.id === viewScope}
                onClick={() => setViewScope(tab.id)}
              >
                {tab.label}
                {tab.id === viewScope && <span className="scopeopt__count">{items.length}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {pending ? (
        <SkeletonPeople />
      ) : (
        <div className="pgrid">
          <button className="ptile ptile--add" type="button" onClick={() => start('new')}>
            <span className="ptile__plus" aria-hidden>
              ＋
            </span>
            <span className="ptile__label">CTAを追加</span>
            <span className="ptile__desc">
              予約ページのURLとボタン文言だけで作れます。サイトから読み取ることもできます。
            </span>
          </button>

          <AnimatePresence mode="popLayout">
            {items.map((cta, index) => (
              <CtaTile key={cta.id} cta={cta} index={index} onOpen={() => setViewing(cta)} />
            ))}
          </AnimatePresence>
        </div>
      )}

      {items.length === 0 && (
        <p className="muted small" style={{ marginTop: 10, marginBottom: 0 }}>
          {viewScope === 'clinic'
            ? 'クリニック共通のCTAはまだありません。'
            : viewScope === 'store'
              ? 'この店舗だけのCTAはまだありません。共通のものは「クリニック共通」で確認できます。'
              : 'まだCTAがありません。'}
        </p>
      )}

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title="CTAテンプレート"
        caption="記事の最後にはこの形で入ります。表示されている内容がそのまま公開されます。"
        wide
      >
        {viewing && (
          <>
            <div className="chips" style={{ marginBottom: 10 }}>
              {viewing.storeId === null ? (
                <Pill>クリニック共通</Pill>
              ) : (
                <Pill tone="muted">{viewing.storeName ?? '店舗'}</Pill>
              )}
              <StatusPill status={viewing.status} />
            </div>
            <CtaCard cta={viewing} />
            <p className="muted small" style={{ marginTop: 10, marginBottom: 0 }}>
              リンク先：{text(viewing.buttonUrl) || '未設定（ボタンは記事に出ません）'}
            </p>
          </>
        )}
        <div className="editpanel__actions">
          <button className="btn btn--sm" type="button" onClick={() => viewing && start(viewing)}>
            編集する
          </button>
          {viewing?.status !== 'APPROVED' ? (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy}
              onClick={() => void approve(viewing, 'APPROVED')}
            >
              承認する
            </button>
          ) : (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy}
              onClick={() => void approve(viewing, 'DRAFT')}
            >
              下書きに戻す
            </button>
          )}
          {viewing?.status !== 'APPROVED' && (
            <span className="muted small">承認するまで、記事のCTAとして選べません。</span>
          )}
        </div>
      </Modal>

      <ModalForm
        open={open !== null}
        onClose={() => setOpen(null)}
        title={editing ? 'CTAを編集' : 'CTAを追加'}
        caption={
          editing
            ? '保存しても状態は変わりません。承認はCTAを開いたところから行います。'
            : '登録した内容は下書きです。承認するまで記事には使われません。'
        }
        wide
        onSubmit={save}
        submitLabel={editing ? '保存する' : '作成する'}
      >
        {/*
          Top to bottom in the order the CTA reads: who it is for, what it is
          called, then what the reader sees - heading, text, button - then the
          channels and the preview. The two section headings that used to split
          this form in half are gone; the order explains itself.
        */}
        {open === 'new' &&
          (isClinicAdmin ? (
            <Field
              label="登録先"
              htmlFor="c-scope"
              span="full"
              hint="クリニック共通にすると、すべての店舗の記事で選べます。店舗を選ぶと、その店舗だけのCTAになります。"
            >
              <Select
                id="c-scope"
                value={newScope}
                onChange={setNewScope}
                options={[
                  { value: '', label: 'クリニック共通（全店舗で使用）' },
                  ...ownStores.map((store) => ({ value: store.id, label: `${store.name}のみ` })),
                ]}
              />
            </Field>
          ) : (
            <p className="notice" role="status">
              登録先：
              <strong>
                {ownStores.find((store) => store.id === newScope)?.name ?? '担当店舗'}
              </strong>
              のCTAとして登録されます。クリニック共通のCTAはクリニック管理者が登録します。
            </p>
          ))}

        <Field
          label="名称"
          htmlFor="c-name"
          required
          span="full"
          hint="一覧で見分けるための管理用の名前です。記事には表示されません。例：無料カウンセリング（LINE優先）"
        >
          <input
            id="c-name"
            value={form.name}
            maxLength={120}
            required
            placeholder="無料カウンセリング"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>

        <Field
          label="見出し"
          htmlFor="c-heading"
          span="full"
          hint="空欄のままでもかまいません。空欄にすると、見出しなしで表示されます。"
        >
          <input
            id="c-heading"
            value={form.heading}
            maxLength={200}
            placeholder="まずは無料カウンセリングでご相談ください"
            onChange={(e) => setForm({ ...form, heading: e.target.value })}
          />
        </Field>

        <Field
          label="本文"
          htmlFor="c-body"
          span="full"
          hint="空欄でもかまいません。予約の流れや所要時間など、読んだ人が次に何をするか分かる内容にします。"
        >
          <Textarea
            id="c-body"
            minRows={3}
            value={form.body}
            maxLength={1000}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
        </Field>

        <Field label="ボタン文言" htmlFor="c-label" hint="空欄なら「詳しくはこちら」になります。">
          <input
            id="c-label"
            value={form.buttonLabel}
            maxLength={60}
            placeholder="無料カウンセリングを予約する"
            onChange={(e) => setForm({ ...form, buttonLabel: e.target.value })}
          />
        </Field>

        <Field
          label="ボタンのリンク先"
          htmlFor="c-url"
          span="wide"
          hint={
            urlOk
              ? 'https から始まるURLです。空欄のときはボタンが表示されません。'
              : '⚠ https:// から始まるURLを入力してください。このままでは保存できません。'
          }
        >
          <input
            id="c-url"
            value={form.buttonUrl}
            maxLength={2000}
            placeholder="https://example.clinic/reserve"
            onChange={(e) => setForm({ ...form, buttonUrl: e.target.value })}
          />
        </Field>

        {/*
          Chips rather than a select: six options, all short, and 「どれで受けて
          いるか」 is answered by seeing which are lit. Its own field block rather
          than <Field>, because a group of buttons has no single control for a
          <label> to point at.
        */}
        <div className="field field--full" role="group" aria-labelledby={channelLabelId}>
          <label id={channelLabelId} htmlFor={channelLabelId}>
            予約手段
          </label>
          <div className="chips">
            {CTA_CHANNELS.map((channel) => (
              <button
                key={channel.value}
                type="button"
                className="chip"
                aria-pressed={form.channels.includes(channel.value)}
                onClick={() => toggleChannel(channel.value)}
              >
                {channel.label}
              </button>
            ))}
          </div>
          <span className="field__hint">
            記事のCTAで案内する導線です。選んだものだけが生成に渡されます。
          </span>
        </div>

        {/*
          What it will look like, beside the fields that decide it. This is the
          same card the article publishes, so there is no second thing to keep in
          step - see CtaCard.
        */}
        <div className="field field--full">
          <FieldGroup title="記事での見え方" note="いま入力されている内容です" />
          <CtaCard
            cta={{
              ...(editing ?? ({} as Cta)),
              heading: form.heading,
              name: form.name,
              body: form.body,
              buttonLabel: form.buttonLabel,
              buttonUrl: form.buttonUrl,
              channels: form.channels,
            }}
          />
        </div>

        {editing && (
          <div className="field field--full">
            <FieldGroup title="このCTAの状態" note="承認したCTAだけが記事で選べます" />
            <p className="muted small" style={{ margin: '0 0 8px' }}>
              現在: <StatusPill status={editing.status} />
            </p>
            <div className="chips">
              {editing.status !== 'APPROVED' ? (
                <button
                  className="btn btn--sm"
                  type="button"
                  disabled={busy}
                  onClick={() => void setStatus('APPROVED')}
                >
                  承認する
                </button>
              ) : (
                <button
                  className="btn btn--sm btn--ghost"
                  type="button"
                  disabled={busy}
                  onClick={() => void setStatus('DRAFT')}
                >
                  下書きに戻す
                </button>
              )}
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                onClick={() => void remove()}
              >
                このCTAを削除
              </button>
            </div>
          </div>
        )}
      </ModalForm>
    </Card>
  );
}
